import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChilletSkull } from "@/components/ChilletSkull";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { API } from "@/lib/api";
import { Field } from "@/components/Atoms";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const toast = useToast();
  const nav = useNavigate();

  // Surface a friendly toast when the user bounced back here after
  // cancelling (or otherwise failing) the Discord OAuth flow. The
  // backend redirects to `/login?oauth_error=…[&oauth_message=…]` in
  // that case — see `auth.discord_oauth_callback`. We also scrub the
  // params out of the URL so a manual reload doesn't re-fire the toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("oauth_error");
    if (!err) return;
    const msg = params.get("oauth_message");
    const human =
      err === "banned"        ? `Your account is banned: ${msg || "No reason provided"}` :
      err === "access_denied" ? "Discord sign-in was cancelled." :
      err === "missing_code"  ? "Discord sign-in was cancelled." :
      msg                     ? `Discord sign-in failed: ${msg}` :
      `Discord sign-in failed (${err}).`;
    toast.error(human);
    // Clean up the URL so reload / share-link don't replay the toast.
    params.delete("oauth_error");
    params.delete("oauth_message");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}`
    );
  }, [toast]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Enter the Crypt" subtitle="Sign in to your ChillPalServers account">
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="login-form">
        <Field label="Email">
          <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
        </Field>
        <Field label="Password">
          <input className="input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
        </Field>
        <button className="btn btn-lg btn-cta" disabled={busy} data-testid="login-submit-button">
          {busy ? "Whispering…" : "Sign in"}
        </button>
        <a href={`${API}/auth/discord`} target="_top" rel="noopener" className="btn btn-discord btn-lg" data-testid="login-discord-button">
          Continue with Discord
        </a>
        <div className="text-center text-sm text-[var(--muted)] mt-2">
          New to ChillPalServers?{" "}
          <Link to="/register" className="text-[var(--teal)]" data-testid="goto-register-link">Create account</Link>
        </div>
        <div className="text-center text-xs text-[var(--dim)]">
          <Link to="/admin/login" data-testid="goto-admin-login-link">Admin login</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const toast = useToast();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(email, username, password);
      toast.success("Welcome to ChillPalServers!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Forge Your Legend" subtitle="Create your ChillPalServers account">
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="register-form">
        <Field label="Email">
          <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email-input" />
        </Field>
        <Field label="Username" hint="2–32 characters; shown on leaderboards.">
          <input className="input" required value={username} onChange={(e) => setUsername(e.target.value)} data-testid="register-username-input" />
        </Field>
        <Field label="Password" hint="At least 8 characters.">
          <input className="input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password-input" />
        </Field>
        <button className="btn btn-lg btn-cta" disabled={busy} data-testid="register-submit-button">
          {busy ? "Binding soul…" : "Create account"}
        </button>
        <a href={`${API}/auth/discord`} target="_top" rel="noopener" className="btn btn-discord btn-lg" data-testid="register-discord-button">
          Continue with Discord
        </a>
        <div className="text-center text-sm text-[var(--muted)] mt-2">
          Already a member?{" "}
          <Link to="/login" className="text-[var(--teal)]" data-testid="goto-login-link">Sign in</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { adminLogin } = useAuth();
  const toast = useToast();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(username, password);
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid admin credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Shop Keeper's Door" subtitle="Restricted to admins of the realm" warning>
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="admin-login-form">
        <Field label="Admin Username">
          <input className="input" required value={username} onChange={(e) => setUsername(e.target.value)} data-testid="admin-username-input" />
        </Field>
        <Field label="Admin Password">
          <input className="input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
        </Field>
        <button className="btn btn-lg btn-cta" disabled={busy} data-testid="admin-login-submit-button">
          {busy ? "Verifying…" : "Sign in as Admin"}
        </button>
        <a href={`${API}/auth/discord?admin=true`} target="_top" rel="noopener" className="btn btn-discord btn-lg" data-testid="admin-discord-button">
          Admin via Discord
        </a>
        <div className="text-center text-sm text-[var(--muted)]">
          <Link to="/login" data-testid="goto-user-login-link">Back to user login</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function AuthCallback() {
  const { consumeOauthCallback } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const access = params.get("access_token");
    const refresh = params.get("refresh_token");
    if (access && refresh) {
      consumeOauthCallback(access, refresh).then(() => nav("/dashboard")).catch(() => nav("/login"));
    } else {
      nav("/login");
    }
    // eslint-disable-next-line
  }, []);

  return (
    <AuthShell title="Returning from the Veil" subtitle="Linking your Discord soul…">
      <div className="flex justify-center py-8">
        <ChilletSkull size={48} className="skull-loader" />
      </div>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, warning, children }) {
  return (
    <div
      className="min-h-screen relative flex items-center justify-center px-4 py-10 skull-cursor overflow-hidden"
      style={{ zIndex: 2 }}
    >
      {/* Full-bleed Palworld fan-art background. We keep it absolutely
          positioned so the form floats above it without affecting
          layout flow. The double-overlay gives us enough contrast for
          form text without washing out the art. */}
      <img
        src="/assets/auth/palworld_fanart.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        data-testid="auth-hero"
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(6,3,10,0.30) 0%, rgba(6,3,10,0.70) 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none backdrop-blur-[2px]"
        aria-hidden="true"
      />

      {/* Brand mark — sits over the art at top-left */}
      <Link
        to="/"
        className="absolute top-6 left-6 flex items-center gap-2 z-10"
        data-testid="auth-brand-logo"
      >
        <ChilletSkull
          size={32}
          className={warning ? "text-[var(--warning)]" : "text-[var(--teal)]"}
          style={{
            filter: `drop-shadow(0 0 12px ${warning ? "rgba(255,107,53,0.6)" : "var(--teal-glow)"})`,
          }}
        />
        <span
          className="text-xl sm:text-2xl glow-teal whitespace-nowrap"
          style={{ fontFamily: "'Cinzel Decorative', serif", letterSpacing: "0.06em" }}
        >
          CHILLPALSERVERS
        </span>
      </Link>

      {/* Translucent form card — relies on `backdrop-blur` so the art
          reads through it. The `glass-card` background is overridden
          here to a lower alpha; everything else (border, glow, radius)
          stays consistent with the rest of the site. */}
      <div
        className="relative z-10 w-full max-w-md p-7 sm:p-9 rounded-2xl fade-up"
        style={{
          background: "rgba(8, 5, 14, 0.55)",
          border: "1px solid rgba(0, 229, 255, 0.18)",
          boxShadow:
            "0 30px 80px -20px rgba(0, 0, 0, 0.85), 0 0 24px rgba(0, 229, 255, 0.15)",
          backdropFilter: "blur(16px) saturate(140%)",
          WebkitBackdropFilter: "blur(16px) saturate(140%)",
        }}
      >
        <h1 className="text-2xl mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
          {title}
        </h1>
        <p className="text-sm text-[var(--muted)] mb-6">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}
